'use strict';
import classNames from 'classnames';
import * as T from 'prop-types';
import React, {Component} from 'react';
import Sidebar from 'react-sidebar';
import {MatchMediaHOC} from '../../../_libs/react-rx-match-media';
import {IonSpinner} from '../../../_libs/reactionic/src/components/ionSpinner';
import {Button} from '../../../components/buttons/Button';
import {isWebBuild} from '../../../config/constants';


export const containerIds = {
  publisher: {
    camera: '#cameraPublisherContainer',
    screen: '#screenPublisherContainer',
  },
  subscriber: {
    camera: '#cameraSubscriberContainer',
    screen: '#screenSubscriberContainer',
  },
};

const connectingMask = msg => (
  <div className="VideoChat-mask">
    <IonSpinner />
    <div className="message with-spinner">
      {msg}
    </div>
  </div>
);

const startCallMask = start => (
  <div className="VideoChat-mask">
    <button
      className="message button clickable"
      onClick={start}
    >
      Click to Join Call
    </button>
  </div>
);

const callErrorMask = () => (
  <div className="VideoChat-mask">
    <button className="message button clickable">
      Failed to Join Call
    </button>
  </div>
);

const chatPanel = close => (
  <div className="VideoChat-chat-panel">
    <div className="ChatList__header">
      <Button
        className="BaseButton ChatList__headerButton close-icon"
        iconClass="icon ion-close"
        onClick={close}
      />
      <span className="VideoChat-chat-header-title">
        Chat
      </span>
    </div>
    <div
      id="chat"
      className="VideoChat-chat-container"
    />
  </div>
);


/**
 * Build classes for container elements based on state
 * @param {Object} props
 */
const containerClasses = (props) => {
  const {active, meta, supportsChat, isLocalAudioEnabled, isLocalVideoEnabled, isChatDisplayed} = props;
  const sharingScreen = meta ? !!meta.publisher.screen : false;
  const viewingSharedScreen = meta ? meta.subscriber.screen : false;
  const activeCameraSubscribers = meta ? meta.subscriber.camera : 0;
  const activeCameraSubscribersGt2 = activeCameraSubscribers > 2;
  const activeCameraSubscribersOdd = activeCameraSubscribers % 2;
  const screenshareActive = viewingSharedScreen || sharingScreen;
  return {
    controlClass: classNames('VideoChat-control-container', {
      // hidden: !active,
    }),
    backClass: classNames('ots-video-control circle VideoChat__iconButton', {
      // must be opposite of .end-call
      hidden: active || props.isWebBuild,
    }),
    cameraSwapClass: classNames('ots-video-control circle VideoChat__iconButton', {
      hidden: !active,
    }),
    localAudioClass: classNames('ots-video-control circle audio', {
      // hidden: !active,
      muted: !isLocalAudioEnabled,
    }),
    localVideoClass: classNames('ots-video-control circle video', {
      // hidden: !active,
      muted: !isLocalVideoEnabled,
    }),
    localCallClass: classNames('ots-video-control circle end-call', {
      hidden: !active,
    }),
    // based on chat module source
    chatButtonClass: classNames('ots-video-control circle text-chat', {
      hidden: !active || !supportsChat,
      enabled: isChatDisplayed,
    }),
    cameraPublisherClass: classNames('video-container', {
      // cover instead of hide so that mobile plugin will get div size
      // hidden: !active,
      small: !props.isWebBuild ? false : (!!activeCameraSubscribers || screenshareActive),
      large: !props.isWebBuild ? true : !(!!activeCameraSubscribers || screenshareActive),
      left: screenshareActive,
    }),
    screenPublisherClass: classNames('video-container', {
      hidden: !active || !sharingScreen,
    }),
    cameraSubscriberClass: classNames('video-container', {
      hidden: !active || !activeCameraSubscribers,
      'active-gt2': activeCameraSubscribersGt2 && !screenshareActive,
      'active-odd': activeCameraSubscribersOdd && !screenshareActive,
      small: !props.isWebBuild ? true : screenshareActive,
      large: !props.isWebBuild ? false : !screenshareActive,
    }),
    screenSubscriberClass: classNames('video-container', {
      hidden: !viewingSharedScreen || !active,
    }),
  };
};

const sidebarStyle = {
  content: {
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
};

// TODO: verify story coverage and impl of the following states: make sure we are accounting for the cases when we never received otCoreOptions
// TODO: add error state data to display if joining a session fails (or connection to the session is lost?)
// TODO: display VideoChatView in loading state
// TODO: change start call to join call if there are others already in the call
// TODO: show people in call once connected and before joining call
// TODO: show video preview in the join call state
// TODO: add audio/video toggle buttons to the join call state
export class BaseVideoChatSessionView extends Component {
  render() {
    const props = this.props;
    const {connected, active, joining, isChatDisplayed, mediaQuery} = props;
    const {
      backClass,
      cameraSwapClass,
      localAudioClass,
      localVideoClass,
      localCallClass,
      chatButtonClass,
      controlClass,
      cameraPublisherClass,
      screenPublisherClass,
      cameraSubscriberClass,
      screenSubscriberClass,
    } = containerClasses(props);

    // TODO if isStateInvalid == true, need to display an error to the user
    return (
      <Sidebar
        rootClassName="VideoChat"
        styles={sidebarStyle}
        sidebar={chatPanel(props.onToggleChat)}
        open={isChatDisplayed}
        docked={isChatDisplayed && !mediaQuery}
        shadow={false}
        touch={false}
        pullRight={true}
        transitions={true}
      >
        <div className="VideoChat-main">
          <div className="VideoChat-video-container">
            {!connected === !joining && !active && connectingMask(connected ? 'Joining Call' : 'Connecting')}
            {connected && !active && !joining && startCallMask(props.onClickStartCall)}
            <div
              id="cameraPublisherContainer"
              className={cameraPublisherClass}
            />
            <div
              id="screenPublisherContainer"
              className={screenPublisherClass}
            />
            <div
              id="cameraSubscriberContainer"
              className={cameraSubscriberClass}
            />
            <div
              id="screenSubscriberContainer"
              className={screenSubscriberClass}
            />
          </div>
          <div
            id="controls"
            className={controlClass}
          >
            <Button
              className={backClass}
              iconClass="ion-arrow-left-c"
              onClick={props.onClickBack}
            />
            <Button
              className={localAudioClass}
              onClick={props.onToggleLocalAudio}
            />
            <Button
              className={localVideoClass}
              onClick={props.onToggleLocalVideo}
            />
            <Button
              className={cameraSwapClass}
              iconClass="ion-ios-reverse-camera-outline"
              disabled={!props.isLocalVideoEnabled}
              onClick={props.onToggleCamera}
            />
            <Button
              className={localCallClass}
              onClick={props.onClickEndCall}
            />
            {/* <Button
              className={chatButtonClass}
              onClick={props.onToggleChat}
            /> */}
          </div>
        </div>
      </Sidebar>
    );
  }
}

BaseVideoChatSessionView.propTypes = {
  className: T.string,
  mediaQuery: T.bool,
  supportsChat: T.bool,

  meta: T.object,
  connected: T.bool,
  joining: T.bool,
  active: T.bool,
  isChatDisplayed: T.bool,
  isLocalAudioEnabled: T.bool,
  isLocalVideoEnabled: T.bool,

  onClickBack: T.func,
  onClickStartCall: T.func,
  onClickEndCall: T.func,
  onToggleCamera: T.func,
  onToggleLocalAudio: T.func,
  onToggleLocalVideo: T.func,
  onToggleChat: T.func,
};

BaseVideoChatSessionView.defaultProps = {
  className: '',
  supportsChat: isWebBuild(),
  isWebBuild: isWebBuild(),
};

export const VideoChatSessionView = MatchMediaHOC(BaseVideoChatSessionView, "(max-width: 900px)");



// WEBPACK FOOTER //
// ./src/js/comments/components/VideoChatSession/VideoChatSessionView.js